import type { inferRouterOutputs } from '@trpc/server';
import { Callout, DataTable, Disclosure } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { AppRouter } from '@/types/appRouter';

type Coverage = NonNullable<inferRouterOutputs<AppRouter>['experiment']['results']['experiment']>['discoveryCorpusCoverage'];
type Group = Coverage['byAsset'][number];

const labels: Record<Group['reason'], string> = {
    invalidPrice: 'Price-quality hold',
    confirmedMissing: 'Confirmed missing history',
    repairPending: 'History repair pending',
    unavailableRequiredEvidence: 'Required history unavailable',
    hydrationFailed: 'Hydration failed'
};
const columns: ColumnDef<Group>[] = [
    { accessorKey: 'symbol', header: 'Symbol' },
    { accessorKey: 'duration', header: 'Timeframe' },
    { id: 'reason', header: 'Reason', cell: ({ row }) => labels[row.original.reason] },
    { accessorKey: 'excludedEntryCount', header: 'Entries skipped' },
    { accessorKey: 'firstSessionDate', header: 'First session' },
    { accessorKey: 'lastSessionDate', header: 'Last session' },
    { id: 'detail', header: 'Detail', cell: ({ row }) => <span className="break-words">{row.original.failureDetail ?? '—'}</span> }
];

/** Entries left out of discovery by symbol. A hydration failure is a defect to look at, not a data hold. */
export const DiscoveryCorpusCoverageCallout = ({ coverage }: { coverage: Coverage }) => {
    if (coverage.excludedEntryCount === 0) return null;
    return (
        <Callout className="mb-6" title="Entries skipped by discovery" variant="warning">
            <p>
                Discovery left out {coverage.excludedEntryCount.toLocaleString()} entries across {coverage.affectedAssetCount} symbols. They
                count toward no discovery result.
            </p>
            {coverage.hydrationFailedEntryCount > 0 && (
                <p className="mt-1 text-sm">
                    {coverage.hydrationFailedEntryCount.toLocaleString()} entries on {coverage.hydrationFailedAssetCount} symbols failed
                    unexpectedly, rather than being held for known data issues. This needs investigating.
                </p>
            )}
            <Disclosure className="mt-3" title="Skipped symbols and reasons">
                <DataTable columns={columns} data={coverage.byAsset} />
                {coverage.truncated && (
                    <p className="mt-2 text-sm">
                        Showing the first {coverage.byAsset.length.toLocaleString()} groups of {coverage.groupCount.toLocaleString()}.
                        Totals include all groups.
                    </p>
                )}
            </Disclosure>
        </Callout>
    );
};
